const FAR_FUTURE_DATE = new Date('December 31, 2999 23:59:59').toUTCString();
const SHARED_DOMAIN = '.tradingterminal.com';

const getHostname = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.hostname.toLowerCase();
};

const normalizeHints = (extraHints) => {
    if (!extraHints) {
        return [];
    }

    if (Array.isArray(extraHints)) {
        return extraHints;
    }

    return [extraHints];
};

const buildHint = (extraHints = []) => {
    return [getHostname(), ...normalizeHints(extraHints)].join(' ').toLowerCase();
};

const isLocalHost = (host) =>
    host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';

const isStagingHint = (hint) =>
    hint.includes('tt-stg') ||
    hint.includes('staging') ||
    hint.includes('-stg') ||
    hint.includes('demo');

const isDevHint = (hint) =>
    hint.includes('tt-dev') ||
    hint.includes('-dev.') ||
    hint.includes('.dev.') ||
    hint.includes('localhost') ||
    hint.includes('127.0.0.1') ||
    hint.includes('development');

/**
 * Resolve env cookie prefix from hostname + optional build-time hints.
 * @param {string|string[]} [extraHints]
 * @returns {'' | 'dev-' | 'stg-'}
 */
const resolveCookiePrefix = (extraHints) => {
    const hint = buildHint(extraHints);

    if (isStagingHint(hint)) {
        return 'stg-';
    }

    if (isDevHint(hint)) {
        return 'dev-';
    }

    return '';
};

/**
 * Cookie names to read for the current environment (strict — no cross-env fallback).
 * @param {string} name
 * @param {string|string[]} [extraHints]
 */
const getCookieReadNames = (name, extraHints) => {
    const prefix = resolveCookiePrefix(extraHints);

    if (prefix === 'stg-') {
        return [`stg-${name}`, `staging-${name}`];
    }

    if (prefix === 'dev-') {
        return [`dev-${name}`];
    }

    return [name];
};

/**
 * Canonical client environment for x-client-env header.
 * @param {string|string[]} [extraHints]
 * @returns {'' | 'local' | 'dev' | 'stg' | 'prod'}
 */
const getClientEnv = (extraHints) => {
    const host = getHostname();

    if (isLocalHost(host)) {
        return 'local';
    }

    const hint = buildHint(extraHints);

    if (isStagingHint(hint)) {
        return 'stg';
    }

    if (isDevHint(hint)) {
        return 'dev';
    }

    if (hint.trim()) {
        return 'prod';
    }

    return '';
};

const getDomainAttribute = () => {
    const host = getHostname();

    if (!host || isLocalHost(host)) {
        return '';
    }

    return `; domain=${SHARED_DOMAIN}`;
};

/** Expire host-only and domain-scoped copies so only one live value remains. */
const expireCookieVariants = (cookieName) => {
    if (typeof document === 'undefined') {
        return;
    }

    const expired = 'Thu, 01 Jan 1970 00:00:00 UTC';
    document.cookie = `${cookieName}=; expires=${expired}; path=/`;
    document.cookie = `${cookieName}=; expires=${expired}; path=/; domain=localhost`;
    document.cookie = `${cookieName}=; expires=${expired}; path=/; domain=${SHARED_DOMAIN}`;
};

/**
 * @param {string} name
 * @param {unknown} value
 * @param {string} [expire]
 * @param {string|string[]} [extraHints]
 */
const setCookie = (name, value, expire = FAR_FUTURE_DATE, extraHints) => {
    const realName = `${resolveCookiePrefix(extraHints)}${name}`;

    expireCookieVariants(realName);

    if (name === 'user') {
        getCookieReadNames(name, extraHints).forEach((candidate) => {
            if (candidate !== realName) {
                expireCookieVariants(candidate);
            }
        });
    }

    document.cookie = `${realName}=${encodeURIComponent(
        JSON.stringify(value)
    )};expires=${expire}; path=/${getDomainAttribute()};`;
};

const setCookieValue = (name, value, expire = FAR_FUTURE_DATE) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expire}; path=/`;
};

/**
 * @param {string} name
 * @param {string|string[]} [extraHints]
 * @returns {string}
 */
const getCookie = (name, extraHints) => {
    if (typeof document === 'undefined' || !document.cookie) {
        return '';
    }

    const cookieArray = document.cookie.split(';');
    const candidateNames = getCookieReadNames(name, extraHints);

    for (let i = 0; i < cookieArray.length; i += 1) {
        const cookie = cookieArray[i].trim();
        const matchedName = candidateNames.find((candidate) => cookie.startsWith(`${candidate}=`));

        if (matchedName) {
            return decodeURIComponent(cookie.substring(`${matchedName}=`.length));
        }
    }

    return '';
};

const getCookieExactName = (name) => {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }

    const cookies = document.cookie.split('; ');

    for (const cookie of cookies) {
        const [key, value] = cookie.split('=');
        if (key === name) {
            return decodeURIComponent(value);
        }
    }

    return null;
};

/**
 * @param {string} name
 * @param {string|string[]} [extraHints]
 */
const removeCookie = (name, extraHints) => {
    const candidateNames = getCookieReadNames(name, extraHints);
    candidateNames.forEach((candidate) => {
        expireCookieVariants(candidate);
    });
};

/**
 * Parse JWT token from env-scoped user cookie.
 * @param {string|string[]} [extraHints]
 * @returns {string}
 */
const parseUserToken = (extraHints) => {
    try {
        const raw = getCookie('user', extraHints);
        if (!raw) {
            return '';
        }

        const parsed = JSON.parse(raw);
        const user = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;

        return typeof user?.token === 'string' ? user.token : '';
    } catch {
        return '';
    }
};

/**
 * Env-scoped cookie names for forced-logout cleanup.
 * @param {string} [host]
 * @param {string|string[]} [extraHints]
 */
const getAuthCookieNamesForEnv = (host, extraHints) => {
    const normalizedHost = (host || getHostname()).toLowerCase();
    const hint = [normalizedHost, ...normalizeHints(extraHints)].join(' ').toLowerCase();

    if (isStagingHint(hint)) {
        return ['stg-user', 'staging-user'];
    }

    if (isDevHint(hint)) {
        return ['dev-user'];
    }

    return ['user'];
};

/**
 * Expire all auth cookie variants for the current environment.
 * @param {string|string[]} [extraHints]
 */
const clearEnvAuthCookies = (extraHints) => {
    if (typeof document === 'undefined') {
        return;
    }

    const cookieNames = getAuthCookieNamesForEnv(getHostname(), extraHints);
    cookieNames.forEach((cookieName) => {
        expireCookieVariants(cookieName);
    });
};

module.exports = {
    FAR_FUTURE_DATE,
    SHARED_DOMAIN,
    resolveCookiePrefix,
    getCookieReadNames,
    getClientEnv,
    getDomainAttribute,
    expireCookieVariants,
    setCookie,
    setCookieValue,
    getCookie,
    getCookieExactName,
    removeCookie,
    parseUserToken,
    getAuthCookieNamesForEnv,
    clearEnvAuthCookies
};
